// src/services/assessmentService.js
import api from './api';

// For development/demo without backend
let mockAssessments = [
    {
        id: '1',
        user_id: '1',
        title: 'Annual Financial Risk Assessment',
        domain: 'financial',
        input_data: {
            financial_data: {
                annual_income: 75000,
                total_debt: 25000,
                credit_history: [
                    { type: 'credit_card', status: 'good', age_months: 36 },
                    { type: 'loan', status: 'good', age_months: 60 }
                ]
            }
        },
        risk_factors: {
            debt_to_income: 0.33,
            credit_history: 0.85
        },
        ai_analysis: {
            analysis: "Based on the provided financial data, this assessment indicates a MEDIUM risk profile. The debt-to-income ratio of 0.33 is within acceptable limits but nearing concerning levels. Credit history is positive with well-established accounts. Recommend monitoring debt levels and avoiding new significant credit obligations in the near term.",
            confidence: 0.87
        },
        risk_score: 4.2,
        created_at: '2025-03-10T14:30:00.000Z',
        updated_at: '2025-03-10T14:30:00.000Z'
    },
    {
        id: '2',
        user_id: '1',
        title: 'Cloud Infrastructure Security Audit',
        domain: 'cybersecurity',
        input_data: {
            security_data: {
                system_details: 'AWS EC2 and S3 deployment with Lambda functions',
                existing_controls: 'IAM roles, VPC, Security Groups, WAF',
                vulnerabilities: [
                    { severity: 'high', count: 2 },
                    { severity: 'medium', count: 8 },
                    { severity: 'low', count: 15 }
                ]
            }
        },
        risk_factors: {
            vulnerability_count: 25,
            threat_level: 0.65
        },
        ai_analysis: {
            analysis: "The security assessment reveals HIGH risk areas that require immediate attention. The two high-severity vulnerabilities relate to misconfigured IAM permissions and outdated security patches. These issues could lead to unauthorized data access. Recommended actions include implementing least-privilege access controls, enhancing monitoring for unusual access patterns, and establishing a regular patch management schedule.",
            confidence: 0.92
        },
        risk_score: 7.8,
        created_at: '2025-04-05T09:15:00.000Z',
        updated_at: '2025-04-05T09:15:00.000Z'
    }
];

let nextId = 3;

export const createAssessment = async (assessmentData) => {
    try {
        // Attempt to call the real API
        const response = await api.post('/risk-assessments/', assessmentData);
        return response.data;
    } catch (error) {
        // If in development and the error is not from a failed API call
        if (process.env.NODE_ENV === 'development' && !error.response) {
            console.log('Using mock data for assessment creation');

            // Create a new mock assessment
            const newAssessment = {
                id: String(nextId++),
                user_id: '1',
                ...assessmentData,
                risk_factors: generateMockRiskFactors(assessmentData),
                ai_analysis: generateMockAIAnalysis(assessmentData),
                risk_score: generateMockRiskScore(assessmentData),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            mockAssessments.push(newAssessment);
            return newAssessment;
        }

        console.error('Error creating assessment:', error);
        throw new Error(error.response?.data?.detail || 'Failed to create assessment');
    }
};

export const getAssessments = async (params = {}) => {
    try {
        const response = await api.get('/risk-assessments/', { params });
        return response.data;
    } catch (error) {
        // If in development and the error is not from a failed API call
        if (process.env.NODE_ENV === 'development' && !error.response) {
            console.log('Using mock data for assessments list');
            return mockAssessments.filter(a => a.user_id === '1');
        }

        console.error('Error fetching assessments:', error);
        throw new Error(error.response?.data?.detail || 'Failed to fetch assessments');
    }
};

export const getAssessmentById = async (id) => {
    try {
        const response = await api.get(`/risk-assessments/${id}`);
        return response.data;
    } catch (error) {
        // If in development and the error is not from a failed API call
        if (process.env.NODE_ENV === 'development' && !error.response) {
            console.log(`Using mock data for assessment ${id}`);
            const assessment = mockAssessments.find(a => a.id === id);
            if (!assessment) {
                throw new Error('Assessment not found');
            }
            return assessment;
        }

        console.error(`Error fetching assessment ${id}:`, error);
        throw new Error(error.response?.data?.detail || 'Failed to fetch assessment');
    }
};

// Helper functions for generating mock data
function generateMockRiskFactors(assessmentData) {
    const domain = assessmentData.domain;

    if (domain === 'financial') {
        const financialData = assessmentData.input_data?.financial_data || {};
        return {
            debt_to_income: financialData.total_debt / Math.max(financialData.annual_income, 1) || 0.4,
            credit_history: 0.75 + (Math.random() * 0.2)
        };
    } else if (domain === 'cybersecurity') {
        return {
            vulnerability_count: Math.floor(5 + Math.random() * 20),
            threat_level: 0.3 + (Math.random() * 0.6)
        };
    } else {
        return {
            general_risk_factor: 0.1 + (Math.random() * 0.8)
        };
    }
}

// Add this to your existing assessmentService.js file

export const deleteAssessment = async (id) => {
    try {
        const response = await api.delete(`/risk-assessments/${id}`);
        return response.data;
    } catch (error) {
        // If in development and the error is not from a failed API call
        if (process.env.NODE_ENV === 'development' && !error.response) {
            console.log(`Using mock data for assessment deletion: ${id}`);

            // Filter out the deleted assessment from mock data
            mockAssessments = mockAssessments.filter(a => a.id !== id);
            return { message: "Assessment deleted successfully" };
        }

        console.error(`Error deleting assessment ${id}:`, error);
        throw new Error(error.response?.data?.detail || 'Failed to delete assessment');
    }
};

function generateMockAIAnalysis(assessmentData) {
    const domain = assessmentData.domain;
    let analysis = '';
    let confidence = 0.7 + (Math.random() * 0.25);

    if (domain === 'financial') {
        const financialData = assessmentData.input_data?.financial_data || {};
        const dti = financialData.total_debt / Math.max(financialData.annual_income, 1) || 0.4;

        if (dti > 0.5) {
            analysis = "HIGH RISK: The debt-to-income ratio exceeds recommended thresholds, indicating potential financial strain. Recommend immediate debt reduction strategies and spending limitations.";
        } else if (dti > 0.3) {
            analysis = "MEDIUM RISK: The debt-to-income ratio is approaching concerning levels. Monitor debt accumulation and consider developing a plan to reduce financial obligations over time.";
        } else {
            analysis = "LOW RISK: The debt-to-income ratio is within healthy parameters. Current financial position appears stable with good debt management practices.";
        }
    } else if (domain === 'cybersecurity') {
        const risks = ["unpatched vulnerabilities", "weak authentication protocols", "insufficient access controls", "inadequate data encryption", "lack of security monitoring"];
        const selectedRisks = risks.sort(() => 0.5 - Math.random()).slice(0, 2).join(" and ");

        analysis = `Security assessment identified potential risks including ${selectedRisks}. Recommend implementing defense-in-depth strategy with regular security audits and employee training.`;
    } else {
        analysis = "Based on the provided data, this assessment indicates potential areas of risk that should be monitored. Recommend developing mitigation strategies and conducting regular follow-up assessments.";
    }

    return {
        analysis,
        confidence
    };
}

function generateMockRiskScore(assessmentData) {
    const domain = assessmentData.domain;

    if (domain === 'financial') {
        const financialData = assessmentData.input_data?.financial_data || {};
        const dti = financialData.total_debt / Math.max(financialData.annual_income, 1) || 0.4;

        // Higher DTI means higher risk
        return Math.min(10, Math.max(1, dti * 10 + (Math.random() * 2 - 1)));
    } else if (domain === 'cybersecurity') {
        // Random but weighted toward higher risk for cybersecurity
        return 5 + (Math.random() * 4);
    } else {
        // Random score
        return 1 + (Math.random() * 9);
    }
}

export default {
    createAssessment,
    getAssessments,
    getAssessmentById
};